import { Button, Card, CardContent, CardHeader, CardTitle, Input, Textarea } from "@aix/ui"
import Link from "@tiptap/extension-link"
import { EditorContent, useEditor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import { Eye, Save, Send, Users } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import toast from "react-hot-toast"
import { trpc } from "~/lib/trpc"

export const CampaignBuilder: React.FC = () => {
  const [campaignName, setCampaignName] = useState("")
  const [subject, setSubject] = useState("")
  const [preheader, setPreheader] = useState("")
  const [fromName, setFromName] = useState("")
  const [fromEmail, setFromEmail] = useState("")
  const [replyTo, setReplyTo] = useState("")
  const [selectedAudience, setSelectedAudience] = useState<string>("")
  const [testEmail, setTestEmail] = useState("")
  const [htmlContent, setHtmlContent] = useState("")

  const { data: audiences } = trpc.emailMarketing.listAudiences.useQuery()

  const createCampaign = trpc.emailMarketing.createCampaign.useMutation({
    onSuccess: (campaign) => {
      toast.success("Campaign created successfully")
      // Reset form
      setCampaignName("")
      setSubject("")
      setPreheader("")
      setHtmlContent("")
    },
    onError: (error) => {
      toast.error(`Failed to create campaign: ${error.message}`)
    },
  })

  const sendCampaign = trpc.emailMarketing.sendCampaign.useMutation({
    onSuccess: (result) => {
      if (result.testMode) {
        toast.success(`Test email sent to ${testEmail}`)
      } else {
        toast.success(`Campaign queued! ${result.queued} emails will be sent.`)
      }
    },
    onError: (error) => {
      toast.error(`Failed to send: ${error.message}`)
    },
  })

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ] as any[],
    content: htmlContent,
    onUpdate: ({ editor }) => {
      setHtmlContent(editor.getHTML())
    },
  })

  const handleCreateAndSend = async () => {
    if (!validateForm()) return

    try {
      const campaign = await createCampaign.mutateAsync({
        name: campaignName,
        subject,
        preheader,
        fromName,
        fromEmail,
        replyTo: replyTo || undefined,
        htmlContent,
        textContent: editor?.getText(),
        audienceId: selectedAudience,
      })

      // Send immediately
      await sendCampaign.mutateAsync({
        campaignId: campaign.id,
        testMode: false,
      })
    } catch (error) {
      // Error already handled by mutations
    }
  }

  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error("Please enter a test email address")
      return
    }

    if (!validateForm()) return

    try {
      const campaign = await createCampaign.mutateAsync({
        name: `${campaignName} (Test)`,
        subject,
        preheader,
        fromName,
        fromEmail,
        replyTo: replyTo || undefined,
        htmlContent,
        textContent: editor?.getText(),
        audienceId: selectedAudience,
      })

      await sendCampaign.mutateAsync({
        campaignId: campaign.id,
        testMode: true,
        testEmail,
      })
    } catch (error) {
      // Error already handled
    }
  }

  const validateForm = (): boolean => {
    if (!campaignName.trim()) {
      toast.error("Please enter a campaign name")
      return false
    }
    if (!subject.trim()) {
      toast.error("Please enter a subject line")
      return false
    }
    if (!fromName.trim()) {
      toast.error("Please enter a from name")
      return false
    }
    if (!fromEmail.trim() || !fromEmail.includes("@")) {
      toast.error("Please enter a valid from email")
      return false
    }
    if (!selectedAudience) {
      toast.error("Please select an audience")
      return false
    }
    if (!htmlContent.trim()) {
      toast.error("Please enter email content")
      return false
    }
    return true
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Campaign Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground">Campaign Name</label>
              <Input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Internal campaign name"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Audience</label>
              <select
                value={selectedAudience}
                onChange={(e) => setSelectedAudience(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="">Select an audience</option>
                {audiences?.map((audience) => (
                  <option key={audience.id} value={audience.id}>
                    {audience.name} ({audience.subscriberCount} subscribers)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Subject Line</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject line"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Preheader Text</label>
              <Input
                value={preheader}
                onChange={(e) => setPreheader(e.target.value)}
                placeholder="Preview text (optional)"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">From Name</label>
              <Input
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Your Name or Company"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">From Email</label>
              <Input
                type="email"
                value={fromEmail}
                onChange={(e) => setFromEmail(e.target.value)}
                placeholder="noreply@yourdomain.com"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Reply-To Email</label>
              <Input
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="Optional (uses from email if empty)"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Content</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="prose prose-sm max-w-none">
              <div className="border border-border rounded-lg p-4 min-h-[400px]">
                <EditorContent editor={editor} />
              </div>
            </div>

            <div className="text-sm text-muted-foreground">
              <p>Available variables:</p>
              <code className="bg-secondary px-2 py-1 rounded">{`{{firstName}}`}</code>{" "}
              <code className="bg-secondary px-2 py-1 rounded">{`{{lastName}}`}</code>{" "}
              <code className="bg-secondary px-2 py-1 rounded">{`{{email}}`}</code>{" "}
              <code className="bg-secondary px-2 py-1 rounded">{`{{unsubscribeLink}}`}</code>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Send Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground">Test Email</label>
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="mt-1"
                />
              </div>
              <Button onClick={handleSendTest} loading={sendCampaign.isLoading} variant="outline" className="mt-6">
                <Eye className="w-4 h-4 mr-2" />
                Send Test
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <Button
                onClick={() =>
                  createCampaign.mutate({
                    name: campaignName,
                    subject,
                    preheader,
                    fromName,
                    fromEmail,
                    replyTo: replyTo || undefined,
                    htmlContent,
                    textContent: editor?.getText(),
                    audienceId: selectedAudience,
                  })
                }
                loading={createCampaign.isLoading}
                variant="outline"
              >
                <Save className="w-4 h-4 mr-2" />
                Save as Draft
              </Button>

              <Button
                onClick={handleCreateAndSend}
                loading={createCampaign.isLoading || sendCampaign.isLoading}
                className="flex-1"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Campaign Now
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
